import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Send } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL;
const HELP = ["piezas", "voluntariado", "difusion", "espacio", "patrocinio", "otro"];

const Field = ({ label, children }) => (
  <label className="block text-left">
    <span className="mono mb-2 block text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

export const SupportForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", help_types: [], message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setForm((f) => ({ ...f, help_types: f.help_types.includes(id) ? f.help_types.filter((x) => x !== id) : [...f.help_types, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.help_types.length) return setError(t("form.errHelp"));
    setError("");
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/supporters`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, lang }) });
      if (!res.ok) throw new Error();
      setStatus("done");
    } catch {
      setStatus("idle");
      setError(t("form.errSend"));
    }
  };

  if (status === "done") {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="support-done" data-testid="support-success">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--green)] text-[#0d0c0a]"><Check size={22} /></span>
        <p className="font-display mt-4 text-3xl">{t("form.thanks", { name: form.name.split(" ")[0] })}</p>
        <p className="mt-2 text-sm text-[var(--muted)]">{t("form.thanksBody", { email: form.email })}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="support-form" data-testid="support-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t("form.name")}>
          <input required minLength={2} maxLength={80} value={form.name} onChange={set("name")} className="support-input" placeholder={t("form.namePh")} data-testid="support-name-input" />
        </Field>
        <Field label={t("form.email")}>
          <input required type="email" maxLength={120} value={form.email} onChange={set("email")} className="support-input" placeholder={t("form.emailPh")} data-testid="support-email-input" />
        </Field>
      </div>
      <div className="mt-6 text-left">
        <span className="mono mb-3 block text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{t("form.how")}</span>
        <div className="flex flex-wrap gap-2">
          {HELP.map((id) => (
            <button type="button" key={id} onClick={() => toggle(id)} className={`support-chip ${form.help_types.includes(id) ? "support-chip-on" : ""}`} data-testid={`support-help-${id}`}>
              {t(`help.${id}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6">
        <Field label={t("form.msg")}>
          <textarea required minLength={3} rows={4} maxLength={1000} value={form.message} onChange={set("message")} className="support-input resize-none" placeholder={t("form.msgPh")} data-testid="support-message-input" />
        </Field>
      </div>
      {error && <p className="mt-4 text-left text-sm text-[#ff8a7a]" data-testid="support-error">{error}</p>}
      <button type="submit" disabled={status === "sending"} className="btn-primary mt-6 w-full justify-center sm:w-auto" data-testid="support-submit-btn">
        {status === "sending" ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />} {t("form.submit")}
      </button>
    </form>
  );
};
